import { useMemo, useState } from "react";
import {
  Check,
  ListChecks,
  Loader2,
  Pencil,
  Plus,
  RefreshCw,
  Trash2,
  X,
} from "lucide-react";
import {
  useProposalRequirements,
  useExtractRequirements,
  useCreateRequirement,
  useUpdateRequirement,
  useDeleteRequirement,
  type RequirementResponse,
  type Section,
} from "@/hooks/use-proposals";

/**
 * Feature C: panel editable de "Requisitos del pliego asignados a esta sección".
 *
 * La IA asigna cada requisito del pliego a una sección (al crear la propuesta
 * desde estructura sugerida); el crítico prioriza los de su sección. Pero la
 * asignación de un LLM es imperfecta, y un mal reparto contamina la evaluación
 * (un requisito mal asignado se evalúa en la sección equivocada). Por eso aquí
 * el usuario puede reasignar / editar / añadir / borrar. Cualquier edición
 * marca el requisito como `is_manual` para que una regeneración no lo pise.
 */

interface SectionRequirementsPanelProps {
  proposalId: string;
  section: Section;
  /** Todas las secciones de la propuesta, para el desplegable de reasignación. */
  sections: Array<Pick<Section, "id" | "title">>;
}

export function SectionRequirementsPanel({
  proposalId,
  section,
  sections,
}: SectionRequirementsPanelProps) {
  const { data: all = [], isLoading } = useProposalRequirements(proposalId);
  const extractMutation = useExtractRequirements(proposalId);
  const createMutation = useCreateRequirement(proposalId);
  const updateMutation = useUpdateRequirement(proposalId);
  const deleteMutation = useDeleteRequirement(proposalId);

  const [expanded, setExpanded] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState("");
  const [adding, setAdding] = useState(false);
  const [newText, setNewText] = useState("");

  const assigned = useMemo(
    () => all.filter((r) => r.section_id === section.id),
    [all, section.id]
  );
  const unassigned = useMemo(() => all.filter((r) => !r.section_id), [all]);

  const startEdit = (r: RequirementResponse) => {
    setEditingId(r.id);
    setEditText(r.text);
  };
  const saveEdit = (r: RequirementResponse) => {
    const text = editText.trim();
    if (text && text !== r.text) {
      updateMutation.mutate({ requirementId: r.id, data: { text } });
    }
    setEditingId(null);
  };
  const reassign = (r: RequirementResponse, sectionId: string) => {
    updateMutation.mutate({
      requirementId: r.id,
      data: { section_id: sectionId || null },
    });
  };
  const addNew = () => {
    const text = newText.trim();
    if (text) {
      createMutation.mutate({ text, section_id: section.id });
    }
    setNewText("");
    setAdding(false);
  };

  const handleRegenerate = () => {
    if (
      all.length > 0 &&
      !window.confirm(
        "Regenerar volverá a extraer los requisitos del pliego con IA. Se " +
          "conservarán los que hayas editado o añadido a mano. ¿Continuar?"
      )
    ) {
      return;
    }
    extractMutation.mutate(undefined, {
      onSuccess: (res) => {
        if (res.notice) window.alert(res.notice);
      },
    });
  };

  const busy = extractMutation.isPending;

  function row(r: RequirementResponse, options: { unassigned?: boolean } = {}) {
    const isEditing = editingId === r.id;
    return (
      <li
        key={r.id}
        className="flex items-start gap-2 px-3 py-2 border border-gray-200 rounded-md bg-white"
      >
        <ListChecks className="w-4 h-4 mt-0.5 flex-shrink-0 text-blue-500" />
        <div className="flex-1 min-w-0">
          {isEditing ? (
            <div className="flex items-center gap-1">
              <input
                autoFocus
                value={editText}
                onChange={(e) => setEditText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") saveEdit(r);
                  if (e.key === "Escape") setEditingId(null);
                }}
                className="flex-1 px-2 py-1 text-sm border border-blue-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-400"
              />
              <button
                type="button"
                onClick={() => saveEdit(r)}
                className="p-1 text-emerald-600 hover:bg-emerald-50 rounded"
                aria-label="Guardar"
              >
                <Check className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => setEditingId(null)}
                className="p-1 text-gray-400 hover:bg-gray-100 rounded"
                aria-label="Cancelar"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm text-gray-800">{r.text}</span>
                {r.category && (
                  <span className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-600">
                    {r.category}
                  </span>
                )}
                {r.is_manual && (
                  <span
                    className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-50 text-amber-700 border border-amber-200"
                    title="Editado o añadido a mano: una regeneración no lo pisará"
                  >
                    manual
                  </span>
                )}
              </div>
              <div className="mt-1.5 flex items-center gap-2">
                {options.unassigned ? (
                  <button
                    type="button"
                    onClick={() => reassign(r, section.id)}
                    className="text-xs text-blue-600 hover:underline"
                  >
                    Asignar a esta sección
                  </button>
                ) : (
                  <select
                    value={r.section_id ?? ""}
                    onChange={(e) => reassign(r, e.target.value)}
                    className="text-xs border border-gray-200 rounded px-1.5 py-0.5 text-gray-600 max-w-[200px]"
                    aria-label="Reasignar requisito a otra sección"
                  >
                    {sections.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.title}
                      </option>
                    ))}
                    <option value="">— Sin asignar —</option>
                  </select>
                )}
              </div>
            </>
          )}
        </div>
        {!isEditing && (
          <div className="flex items-center gap-0.5">
            <button
              type="button"
              onClick={() => startEdit(r)}
              className="p-1 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded"
              aria-label="Editar requisito"
            >
              <Pencil className="w-3.5 h-3.5" />
            </button>
            <button
              type="button"
              onClick={() => deleteMutation.mutate(r.id)}
              className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
              aria-label="Eliminar requisito"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
      </li>
    );
  }

  return (
    <div className="border border-gray-200 rounded-lg bg-gray-50">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3"
      >
        <span className="flex items-center gap-2 text-sm font-semibold text-gray-700">
          <ListChecks className="w-4 h-4 text-blue-600" />
          Requisitos del pliego de esta sección
          <span className="px-1.5 py-0.5 rounded-full text-xs bg-blue-100 text-blue-700">
            {assigned.length}
          </span>
        </span>
        <span className="text-xs text-gray-400">{expanded ? "▲" : "▼"}</span>
      </button>

      {expanded && (
        <div className="px-4 pb-4 space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-xs text-gray-500">
              Lo que esta sección debe cubrir según el pliego. El crítico los
              prioriza al evaluar. Puedes reasignar, editar, añadir o borrar.
            </p>
          </div>

          {isLoading ? (
            <div className="flex items-center gap-2 text-sm text-gray-500 py-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Cargando…
            </div>
          ) : (
            <>
              {assigned.length > 0 ? (
                <ul className="space-y-2">{assigned.map((r) => row(r))}</ul>
              ) : (
                <p className="text-sm text-gray-500 py-2">
                  Aún no hay requisitos asignados a esta sección.
                </p>
              )}

              {/* Añadir manual */}
              {adding ? (
                <div className="flex items-center gap-1">
                  <input
                    autoFocus
                    value={newText}
                    onChange={(e) => setNewText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") addNew();
                      if (e.key === "Escape") {
                        setAdding(false);
                        setNewText("");
                      }
                    }}
                    placeholder="Nuevo requisito…"
                    className="flex-1 px-2 py-1 text-sm border border-blue-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-400"
                  />
                  <button
                    type="button"
                    onClick={addNew}
                    className="p-1 text-emerald-600 hover:bg-emerald-50 rounded"
                    aria-label="Guardar nuevo requisito"
                  >
                    <Check className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setAdding(false);
                      setNewText("");
                    }}
                    className="p-1 text-gray-400 hover:bg-gray-100 rounded"
                    aria-label="Cancelar"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setAdding(true)}
                  className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700"
                >
                  <Plus className="w-4 h-4" /> Añadir requisito
                </button>
              )}

              {/* Sin asignar: reachable desde aquí para no perderlos */}
              {unassigned.length > 0 && (
                <div className="pt-2 border-t border-gray-200">
                  <p className="text-xs font-medium text-amber-700 mb-1.5">
                    Sin asignar ({unassigned.length}) — asígnalos a la sección
                    que corresponda
                  </p>
                  <ul className="space-y-2">
                    {unassigned.map((r) => row(r, { unassigned: true }))}
                  </ul>
                </div>
              )}

              {/* Regenerar con IA */}
              <div className="pt-2 border-t border-gray-200">
                <button
                  type="button"
                  onClick={handleRegenerate}
                  disabled={busy}
                  className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-600 hover:text-gray-900 disabled:opacity-50"
                >
                  {busy ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <RefreshCw className="w-3.5 h-3.5" />
                  )}
                  Regenerar con IA (conserva los manuales)
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default SectionRequirementsPanel;
